// The guest's chosen reason → the enum initializeRefund takes. See README.md § Refund amount.
//
// Two encodings of the same choice, NOT interchangeable: ReasonValue is a free-text Intercom
// audience predicate matched by exact string; GuestCurrencyRefundReason is a schema enum the refund
// service validates. This map is the only place the two are tied together. Ported from iOS
// `DeliveryRefundCoordinator.makeMenuStep`; keep the entries in menu order to diff them by eye.
import { GuestCurrencyRefundReason } from 'src/apollo/onlineOrdering'
import { ReasonValue, ReasonValueType } from './reasons'

// `Record<ReasonValueType, …>` deliberately, not a lookup with a fallback: adding a ReasonValue
// without deciding its refund reason then fails to compile, which is the only automated check here
// — a wrong-but-valid enum would quote under the wrong reason and nothing would complain.
export const REFUND_REASON_BY_VALUE: Record<
  ReasonValueType,
  // null for the two sub-menu parents — never the reason a refund is quoted under.
  GuestCurrencyRefundReason | null
> = {
  [ReasonValue.orderNeverArrived]: null,
  [ReasonValue.noDeliveryConfirmation]:
    GuestCurrencyRefundReason.NeverDeliveredNoDeliveryConfirmation,
  [ReasonValue.deliveryDetailsMismatch]:
    GuestCurrencyRefundReason.NeverDeliveredProofAddressConflict,
  [ReasonValue.markedDeliveredStillMoving]:
    GuestCurrencyRefundReason.NeverDeliveredDriverMoving,
  [ReasonValue.itemsMissingOrIncorrect]:
    GuestCurrencyRefundReason.ItemMissingIncorrect,
  [ReasonValue.orderArrivedLate]: GuestCurrencyRefundReason.OrderArrivedLate,
  [ReasonValue.orderArrivedDamagedOrPoor]: null,
  [ReasonValue.damagedOrSpilled]: GuestCurrencyRefundReason.FoodDamagedSpilled,
  [ReasonValue.coldOrSoggy]: GuestCurrencyRefundReason.FoodColdSoggy,
  // ORDER_INCORRECT, not ORDER_ARRIVED_LATE as TDS-2914 tabulates: the ticket copied iOS while a
  // `// TODO: needs its own reason` placeholder was still there. iOS now sends `.orderIncorrect`,
  // and ORDER_ARRIVED_LATE would file a wrong-order claim as a late one.
  [ReasonValue.receivedWrongOrder]: GuestCurrencyRefundReason.OrderIncorrect,
  // Manual review still quotes, so "Something else" needs a real reason enum like every leaf.
  [ReasonValue.somethingElse]: GuestCurrencyRefundReason.Other
}

// The reason to quote under, or null when there is nothing to quote. Callers must not substitute a
// default: quoting under the wrong reason reserves a real refund against a claim never made.
export function refundReasonFor(
  value: ReasonValueType | undefined
): GuestCurrencyRefundReason | null {
  return value ? REFUND_REASON_BY_VALUE[value] : null
}
